import { Property } from '../types';
import { PROPERTY_STORAGE_KEY } from '../constants';
import { generateUUID } from './formatters';

export const loadPortfolioFromStorage = (): Property[] => {
  try {
    const savedData = localStorage.getItem(PROPERTY_STORAGE_KEY);
    if (savedData) {
      const parsed = JSON.parse(savedData);
      if (Array.isArray(parsed)) {
        // Add default values for new properties that may not exist in saved data
        return parsed.map(property => ({
          ...property,
          inputs: {
            ...property.inputs,
            instandhaltungsruecklage: property.inputs.instandhaltungsruecklage ?? 0,
            verwaltungskosten: property.inputs.verwaltungskosten ?? 0
          }
        }));
      }
      return [];
    }
  } catch (error) {
    console.error('Error loading portfolio from storage:', error);
  }
  return [];
};

export const savePortfolioToStorage = (portfolio: Property[]): void => {
  try {
    localStorage.setItem(PROPERTY_STORAGE_KEY, JSON.stringify(portfolio));
  } catch (error) {
    console.error('Error saving portfolio to storage:', error);
  }
};

export const exportPortfolioToCSV = (portfolio: Property[]): void => {
  if (portfolio.length === 0) {
    alert('Keine Immobilien zum Exportieren vorhanden.');
    return;
  }
  
  // Create CSV headers
  const headers = ['ID', 'Name', 'Favorit', 'Besichtigung', 'Stadtgebiet', 'Lage', 'Wohnfläche', 
    'Zimmer', 'Stellplatz', 'Inseratpreis', 'Angebotspreis', 'Baujahr', 'Gebäudeanteil', 
    'AfA-Satz', 'Steuersatz', 'Finanzierung', 'Zinssatz', 'Tilgung', 'Makler', 'Notar', 
    'Grundbuchamt', 'Grunderwerbsteuer', 'Hausgeld Umlagefähig', 'Hausgeld Nicht-Umlagefähig',
    'IST Kaltmiete/m²', 'IST Stellplatzmiete', 'SOLL Kaltmiete/m²', 'SOLL Stellplatzmiete',
    'Mietentwicklung %/Jahr', 'Inflation %/Jahr', 'Wertzuwachs %/Jahr', 'Instandhaltung €/m²',
    'Instandhaltung J10 €/m²', 'Mietausfall %', 'Anfangsinvestitionen', 'Investitionen J10',
    'Schwierigkeit', 'Sofortabschreibung', 'Positive Merkmale', 'Negative Merkmale'];
  
  // Create CSV rows
  const rows = portfolio.map(property => {
    const inputs = property.inputs;
    return [
      property.id,
      property.name,
      property.isFavorite ? 'Ja' : 'Nein',
      inputs.besichtigung,
      inputs.stadtgebiet,
      inputs.lage,
      inputs.wohnflaeche,
      inputs.zimmer,
      inputs.stellplatzTyp,
      inputs.inseratpreis,
      inputs.angebotspreis,
      inputs.baujahr,
      inputs.gebaudeanteil,
      inputs.afaSatz,
      inputs.steuersatz,
      inputs.finanzierungsanteil,
      inputs.zinssatz,
      inputs.tilgung,
      inputs.makler,
      inputs.notar,
      inputs.grundbuchamt,
      inputs.grunderwerbst,
      inputs.hausgeldUmlagefaehig,
      inputs.hausgeldNichtUmlagefaehig,
      inputs.istKaltmieteProQm,
      inputs.istStellplatzMiete,
      inputs.kaltmieteProQm,
      inputs.stellplatzMiete,
      inputs.mietentwicklungProJahr,
      inputs.inflationProJahr,
      inputs.wertzuwachsProJahr,
      inputs.instandhaltung,
      inputs.instandhaltungJ10,
      inputs.mietausfallrisiko,
      inputs.anfangsinvestitionen,
      inputs.investitionenJahr10,
      inputs.schwierigkeit,
      inputs.sofortabschreibungAktivieren ? 'Ja' : 'Nein',
      `"${inputs.positiveMerkmale.replace(/"/g, '""')}"`,
      `"${inputs.negativeMerkmale.replace(/"/g, '""')}"`
    ];
  });
  
  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map(row => row.join(';'))
    .join('\n');
  
  // Create and download the file
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.href = url;
  link.download = `immobilien_portfolio_${new Date().toISOString().slice(0, 10)}.csv`;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

export const importPortfolioFromCSV = (file: File): Promise<Property[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          throw new Error('CSV file is empty or invalid');
        }
        
        // Skip header row
        const dataRows = lines.slice(1);
        
        const properties: Property[] = dataRows.map(row => {
          // Parse CSV considering quoted fields
          const fields: string[] = [];
          let currentField = '';
          let insideQuotes = false;
          
          for (let i = 0; i < row.length; i++) {
            const char = row[i];
            
            if (char === '"') {
              if (insideQuotes && row[i + 1] === '"') {
                currentField += '"';
                i++;
              } else {
                insideQuotes = !insideQuotes;
              }
            } else if (char === ';' && !insideQuotes) {
              fields.push(currentField);
              currentField = '';
            } else {
              currentField += char;
            }
          }
          fields.push(currentField);
          
          // Create property object from fields
          return {
            id: fields[0] || generateUUID(),
            name: fields[1] || 'Importierte Immobilie',
            isFavorite: fields[2] === 'Ja',
            inputs: {
              propertyName: fields[1] || 'Importierte Immobilie',
              besichtigung: fields[3] || '',
              stadtgebiet: fields[4] || 'Zentrum',
              lage: fields[5] || 'B',
              wohnflaeche: parseFloat(fields[6]) || 80,
              zimmer: parseInt(fields[7]) || 3,
              stellplatzTyp: (fields[8] as 'Nein' | 'TG' | 'St.Platz') || 'Nein',
              inseratpreis: parseFloat(fields[9]) || 350000,
              angebotspreis: parseFloat(fields[10]) || 320000,
              baujahr: parseInt(fields[11]) || 2010,
              gebaudeanteil: parseFloat(fields[12]) || 80,
              afaSatz: parseFloat(fields[13]) || 2.0,
              steuersatz: parseFloat(fields[14]) || 42.0,
              finanzierungsanteil: parseFloat(fields[15]) || 100,
              zinssatz: parseFloat(fields[16]) || 4.4,
              zinssatzJ10: parseFloat(fields[16]) || 5.0, // Use same as zinssatz if not present
              tilgung: parseFloat(fields[17]) || 1.0,
              makler: parseFloat(fields[18]) || 3.57,
              notar: parseFloat(fields[19]) || 1.5,
              grundbuchamt: parseFloat(fields[20]) || 0.5,
              grunderwerbst: parseFloat(fields[21]) || 3.5,
              hausgeldUmlagefaehig: parseFloat(fields[22]) || 150,
              hausgeldNichtUmlagefaehig: parseFloat(fields[23]) || 100,
              istKaltmieteProQm: parseFloat(fields[24]) || 10,
              istStellplatzMiete: parseFloat(fields[25]) || 0,
              kaltmieteProQm: parseFloat(fields[26]) || 12,
              stellplatzMiete: parseFloat(fields[27]) || 50,
              mietentwicklungProJahr: parseFloat(fields[28]) || 2.25,
              inflationProJahr: parseFloat(fields[29]) || 2.25,
              wertzuwachsProJahr: parseFloat(fields[30]) || 1.5,
              instandhaltung: parseFloat(fields[31]) || 10,
              instandhaltungJ10: parseFloat(fields[32]) || 15,
              mietausfallrisiko: parseFloat(fields[33]) || 2,
              anfangsinvestitionen: parseFloat(fields[34]) || 0,
              investitionenJahr10: parseFloat(fields[35]) || 0,
              schwierigkeit: parseInt(fields[36]) || 3,
              sofortabschreibungAktivieren: fields[37] === 'Ja',
              positiveMerkmale: fields[38] || '',
              negativeMerkmale: fields[39] || '',
              instandhaltungsruecklage: 0,
              verwaltungskosten: 0,
              // LOI fields with defaults
              loiSender: '',
              loiRecipient: '',
              loiDate: new Date().toISOString().slice(0, 10),
              loiOfferAmount: 0,
              loiIntroduction: '',
              loiPositiveAspects: '',
              loiPriceReducingAspects: '',
              loiConclusion: '',
              loiConditions: '',
              // Additional LOI fields
              kauferName: '',
              kauferAdresse: '',
              kauferPLZ: '',
              kauferStadt: '',
              kauferTelefon: '',
              kauferEmail: '',
              verkauferAnrede: 'Herr',
              verkauferName: '',
              verkauferAdresse: '',
              verkauferPLZ: '',
              verkauferStadt: '',
              objektbezeichnung: '',
              adresse: '',
              plz: '',
              stadt: '',
              uebergabedatum: '',
              kaufpreisText: '',
              finanzierungsvorbehalt: '',
              zusaetzlicheBedingungen: '',
              notarName: '',
              gueltigkeitTage: 14
            }
          };
        });
        
        resolve(properties);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

